import { type SafeReadonly } from "@apollo/client/cache/core/types/common"
import { type FieldFunctionOptions } from "@apollo/client/cache/inmemory/policies"
import {
  type Feature,
  FeatureKind,
  type Format,
  FormatClass,
  type Segment,
} from "@digits-graphql/frontend/graphql-bearer"
import { type FeatureValue, type TextFeatureValue } from "@digits-graphql/frontend/graphql-pay"
import {
  mergeArrays,
  mergeArraysBy,
} from "src/shared/initializers/typePolicies/helpers/mergeReducers"

// A segment's identity is its file: a bill format can hold the pages of several
// source documents, and each document's sequence runs from 0.
const mergeSegments = mergeArraysBy((s: Segment) => `${s.collectionId}:${s.fileId}`)

export const vaultFields = {
  RawDocument: {
    fields: {
      // listDocuments answers with a reduced projection — the source format plus
      // ImageSmall — so a Vault-list poll must not drop the ImageLarge a document
      // read wrote. Formats are append-only, so a union never holds stale data.
      formats: {
        merge: mergeArrays,
      },
      // A rename supersedes the old feature server-side, so incoming replaces existing.
      features: {
        merge: false,
      },

      title: {
        read(_: SafeReadonly<string>, { readField, canRead }: FieldFunctionOptions) {
          const featuresRefs = readField<Feature[]>("features")
          if (!featuresRefs) return null

          const titleRef = featuresRefs.find(
            (f) => canRead(f) && readField("kind", f) === FeatureKind.Title
          )

          if (!titleRef) return null
          const value = readField<FeatureValue>("value", titleRef)
          return value && isTextFeatureValue(value) ? value.textValue : null
        },
      },

      fileName: {
        read(_: SafeReadonly<string>, { readField, canRead }: FieldFunctionOptions) {
          const sourceFormat = readField<Format>("sourceFormat")
          if (!sourceFormat) return null
          return readField<Segment[]>("segments", sourceFormat)?.[0]?.fileName ?? null
        },
      },

      fileSize: {
        read(_: SafeReadonly<number>, { readField, canRead }: FieldFunctionOptions) {
          const sourceFormat = readField<Format>("sourceFormat")
          if (!sourceFormat) return null
          return readField<Segment[]>("segments", sourceFormat)?.[0]?.fileSize ?? null
        },
      },

      mimeType: {
        read(_: SafeReadonly<number>, { readField, canRead }: FieldFunctionOptions) {
          const sourceFormat = readField<Format>("sourceFormat")
          if (!sourceFormat) return null
          return readField<Segment[]>("mimeType", sourceFormat) ?? null
        },
      },

      thumbnail: {
        read(_: SafeReadonly<number>, { readField, canRead }: FieldFunctionOptions) {
          const formatsRefs = readField<Format[]>("formats")
          if (!formatsRefs) return null

          const imageThumb = formatsRefs.find(
            // use ImageSmall as the preview image as ImageThumbnail is too blurry
            (f) => canRead(f) && readField("formatClass", f) === FormatClass.ImageSmall
          )
          if (!imageThumb) return null

          return readField<Segment[]>("segments", imageThumb)?.find((s) => s.sequence === 0) || null
        },
      },

      sourceFormat: {
        read(_: SafeReadonly<number>, { readField, canRead }: FieldFunctionOptions) {
          const sourceFormatId = readField<string>("sourceFormatId")
          const formatsRefs = readField<Format[]>("formats")

          if (!sourceFormatId || !formatsRefs) return null

          return (
            formatsRefs.find((f) => canRead(f) && readField("id", f) === sourceFormatId) || null
          )
        },
      },
    },
  },

  Format: {
    fields: {
      // listDocuments answers with page 0 only, so a list poll must not drop the later pages.
      segments: {
        merge: mergeSegments,
      },
    },
  },
}

function isTextFeatureValue(value: FeatureValue): value is TextFeatureValue {
  return (value as TextFeatureValue).textValue !== undefined
}
